
import { displayName } from '../lib/displayName';
import React, { useState, useEffect } from 'react';
import { generateStickerIcons, processImageWithTransparency, addStickerContour } from '../lib/ai';
import { expectedAiSeconds, recordAiDuration } from '../lib/extras';
import { IconCategory } from '../types';

interface AiIconGeneratorProps {
  onIconGenerated: (base64Image: string, prompt: string, targetCategoryId?: string) => void;
  onCancel: () => void;
  categories: IconCategory[];
  isAdmin: boolean;
}

interface ImageCandidate {
    raw: string;
    processed: string;
    final: string; 
}

const AiIconGenerator: React.FC<AiIconGeneratorProps> = ({ onIconGenerated, onCancel, categories, isAdmin }) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [remaining, setRemaining] = useState(0);
  useEffect(() => {
    if (!isLoading) return;
    const t = setInterval(() => setRemaining(r => Math.max(0, r - 1)), 1000);
    return () => clearInterval(t);
  }, [isLoading]);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ImageCandidate[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [useStickerBorder, setUseStickerBorder] = useState(true);
  const [targetCategory, setTargetCategory] = useState<string>('');
  const [isSavingStep, setIsSavingStep] = useState(false);

  useEffect(() => {
      if (categories.length > 0 && !targetCategory) setTargetCategory(categories[0].id);
  }, [categories, targetCategory]);

  useEffect(() => {
      const updateImages = async () => {
          if (candidates.length === 0) return;
          const newCandidates = await Promise.all(candidates.map(async (c) => {
             let transparent = c.processed || await processImageWithTransparency(c.raw, 'white');
             let final = useStickerBorder ? await addStickerContour(transparent) : transparent;
             return { ...c, processed: transparent, final: final };
          }));
          if (newCandidates[0].final !== candidates[0].final) setCandidates(newCandidates);
      };
      updateImages();
  }, [useStickerBorder]);

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim()) return;
    setIsLoading(true); setError(null); setCandidates([]); setSelectedIndex(0); setIsSavingStep(false); setUseStickerBorder(true);
    setRemaining(expectedAiSeconds());
    const t0 = Date.now();
    try {
      const rawImages = await generateStickerIcons(prompt);
      recordAiDuration(Date.now() - t0);
      const processedPromises = rawImages.map(raw => processImageWithTransparency(raw, 'white'));
      const processedImages = await Promise.all(processedPromises);
      const finalImages = await Promise.all(processedImages.map(p => addStickerContour(p)));
      setCandidates(rawImages.map((raw, i) => ({ raw, processed: processedImages[i], final: finalImages[i] })));
    } catch (err: any) { setError(err.message || "Chyba při generování."); }
    finally { setIsLoading(false); }
  };

  const getSelectedImage = () => candidates[selectedIndex]?.final;

  return (
    <div className="bg-white p-6 rounded-md border-4 border-gray-900 shadow-2xl animate-fade-in mb-8">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-sm font-black text-gray-900 flex items-center gap-3 uppercase tracking-[0.2em]">
          <div className="w-1.5 h-5 bg-red-600 rounded-sm"></div>
          AI NÁSTROJ GENERACE
        </h3>
        <button onClick={onCancel} className="text-gray-400 hover:text-black">
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      {candidates.length === 0 ? (
        <form onSubmit={handleGenerate} className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2">Popište požadovaný objekt</label>
            <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="Např. roztomilé koťátko, fotbalový míč, vesmírná raketa..." className="w-full px-5 py-4 border-2 border-gray-200 rounded-md focus:border-black bg-gray-50 text-sm font-bold shadow-inner min-h-[100px] outline-none" autoFocus />
          </div>
          {isLoading && <p className="text-[11px] text-gray-500 text-center -mt-2">Připravujeme až 4 návrhy najednou, vyberete si ten nejlepší.</p>}
          {error && <div className="p-4 text-xs font-bold bg-red-50 text-red-700 border-2 border-red-100 uppercase tracking-widest rounded-md">{error}</div>}
          <button type="submit" disabled={isLoading || !prompt.trim()} className="w-full py-4 bg-gray-900 text-white font-black uppercase tracking-[0.3em] rounded-md shadow-xl transition-all disabled:opacity-50 active:scale-[0.98]">
            {isLoading ? (remaining > 0 ? `GENERUJI… ZBÝVÁ CCA ${remaining} s` : 'GENERUJI… JEŠTĚ CHVILKU') : 'VYGENEROVAT NÁVRHY'}
          </button>
        </form>
      ) : isSavingStep && isAdmin ? (
        <div className="space-y-6 animate-fade-in">
           <div className="flex flex-col sm:flex-row gap-6 p-6 bg-gray-50 border-2 border-gray-200 rounded-md shadow-inner">
               <div className="w-32 h-32 flex-shrink-0 bg-white border-2 border-black p-2 rounded-md">
                   <img src={getSelectedImage()} alt="Candidate" className="w-full h-full object-contain" />
               </div>
               <div className="flex-1 space-y-4">
                   <h4 className="font-black text-gray-900 uppercase text-xs tracking-widest">Zařazení do katalogu</h4>
                   <select value={targetCategory} onChange={(e) => setTargetCategory(e.target.value)} className="w-full text-xs font-bold border-2 border-gray-200 rounded-md bg-white p-3 outline-none focus:border-black appearance-none shadow-sm">
                       {categories.map(cat => <option key={cat.id} value={cat.id}>{displayName(cat.name)}</option>)}
                   </select>
               </div>
           </div>
           <div className="flex gap-4">
              <button onClick={() => setIsSavingStep(false)} className="px-6 py-3 bg-white text-gray-900 font-black uppercase text-[10px] tracking-widest border-2 border-gray-200 hover:bg-gray-50 transition-all rounded-md">Zpět</button>
              <button onClick={() => onIconGenerated(getSelectedImage()!, prompt, targetCategory)} className="flex-1 py-4 bg-green-600 text-white font-black uppercase text-[10px] tracking-[0.2em] shadow-lg hover:bg-green-700 transition-all rounded-md">Uložit do katalogu</button>
           </div>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="flex flex-wrap justify-evenly gap-3">
            {candidates.map((c, idx) => (
                <div key={idx} onClick={() => setSelectedIndex(idx)} className={`w-28 sm:w-32 aspect-square rounded-md border-2 p-1 cursor-pointer transition-all ${selectedIndex === idx ? 'border-red-600 ring-4 ring-red-50 bg-white' : 'border-gray-200 hover:border-gray-400 bg-gray-50 shadow-sm'}`}>
                    <img src={c.final} alt="Option" className="w-full h-full object-contain" />
                </div>
            ))}
          </div>
          <label className="flex items-center gap-3 p-4 bg-gray-50 border-2 border-gray-200 cursor-pointer w-full rounded-md transition-all hover:bg-gray-100 shadow-inner">
              <input type="checkbox" checked={useStickerBorder} onChange={(e) => setUseStickerBorder(e.target.checked)} className="h-6 w-6 text-black border-2 border-gray-300 rounded focus:ring-black" />
              <span className="text-[10px] font-black text-gray-900 uppercase tracking-widest">Přidat bílý okraj (efekt samolepky)</span>
          </label>
          <div className="flex flex-col gap-3">
             {isAdmin && <button onClick={() => setIsSavingStep(true)} className="w-full py-4 bg-gray-900 text-white font-black uppercase text-[10px] tracking-[0.2em] shadow-xl hover:bg-black rounded-md transition-all">Uložit do katalogu...</button>}
             <div className="flex gap-3">
                <button onClick={() => setCandidates([])} className="flex-1 py-3 bg-white border-2 border-gray-200 text-gray-900 font-black uppercase text-[10px] tracking-widest hover:bg-gray-50 rounded-md transition-all">Znovu</button>
                <button onClick={() => onIconGenerated(getSelectedImage()!, prompt, undefined)} className="flex-1 py-4 bg-red-600 text-white font-black uppercase text-[10px] tracking-[0.2em] shadow-xl hover:bg-red-700 rounded-md transition-all">Použít vybranou</button>
             </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiIconGenerator;
